import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { axiosInstancev2 } from '../utils/Axios/axiosInterceptorv2';

// Forms from forms.powerlearnprojectafrica.org assigned to cohorts, and
// who has answered (user_mngt forms module; lms_backend docs/api/lms-forms.md).
const BASE = '/forms';
const KEY = 'form-assignments';
export const PAGE_SIZE = 50;

const options = { refetchOnWindowFocus: false, retry: false };

const clean = (params) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== ''
    )
  );

export const useAvailableForms = (q, enabled = true) =>
  useQuery(
    [KEY, 'available', q],
    async () =>
      (await axiosInstancev2.get(`${BASE}/available`, { params: clean({ q }) }))
        .data?.forms ?? [],
    { ...options, enabled }
  );

export const useAssignments = (filters, offset = 0) => {
  const params = clean({
    cohort_id: filters.cohortId,
    status: filters.status,
    q: filters.q,
    limit: PAGE_SIZE,
    offset,
  });
  return useQuery(
    [KEY, 'list', params],
    async () =>
      (await axiosInstancev2.get(`${BASE}/assignments`, { params })).data,
    { ...options, keepPreviousData: true }
  );
};

export const useAssignment = (id) =>
  useQuery(
    [KEY, 'one', id],
    async () => (await axiosInstancev2.get(`${BASE}/assignments/${id}`)).data,
    { ...options, enabled: Boolean(id) }
  );

export const useAssignmentStudents = (id, filters, offset = 0) => {
  const params = clean({
    status: filters.status,
    q: filters.q,
    limit: PAGE_SIZE,
    offset,
  });
  return useQuery(
    [KEY, 'students', id, params],
    async () =>
      (
        await axiosInstancev2.get(`${BASE}/assignments/${id}/students`, {
          params,
        })
      ).data,
    { ...options, enabled: Boolean(id), keepPreviousData: true }
  );
};

const useFormsMutation = (fn) => {
  const queryClient = useQueryClient();
  return useMutation(fn, {
    onSuccess: () => queryClient.invalidateQueries([KEY]),
  });
};

export const useCreateAssignments = () =>
  useFormsMutation(
    async (body) =>
      (await axiosInstancev2.post(`${BASE}/assignments`, clean(body))).data
  );

// due_at: null clears the due date, so it's sent as is.
export const useUpdateAssignment = (id) =>
  useFormsMutation(
    async (changes) =>
      (await axiosInstancev2.put(`${BASE}/assignments/${id}`, changes)).data
  );

export const useArchiveAssignment = (id) =>
  useFormsMutation(
    async (archive) =>
      (
        await axiosInstancev2.post(
          `${BASE}/assignments/${id}/${archive ? 'archive' : 'restore'}`
        )
      ).data
  );

// Without ids: everyone who hasn't submitted.
export const useRemind = (id) =>
  useFormsMutation(
    async (studentIds) =>
      (
        await axiosInstancev2.post(
          `${BASE}/assignments/${id}/remind`,
          studentIds?.length ? { student_ids: studentIds } : {}
        )
      ).data
  );

export const exportPath = (id) => `${BASE}/assignments/${id}/export`;

export const formsUnavailable = (error) => {
  const data = error?.response?.data;
  return error?.response?.status === 404 && !(data?.message || data?.error);
};

export const formsError = (error, fallback) => {
  if (formsUnavailable(error)) {
    return "Forms aren't available on the server yet.";
  }
  const data = error?.response?.data;
  return data?.message || data?.error || fallback;
};
